import { TemplateNodeVisitor } from ".";
import { FormNode, PageNode, SectionNode, TableNode, RowNode, CellNode, DropdownGroupNode, RadioButtonGroupNode, DropdownElementNode, RadioButtonElementNode, LabelNode, TextBoxNode, CheckboxNode, DateNode, InvalidNode, ElementNode, ImageNode } from "..";
import { FormPack, Form, ElementPack, PagePack, Page, Element } from "../../models";
import { InputNode } from "../primitiveNodes";
import { ElementAttributeType } from "../../enums";

const getAttr = (type: ElementAttributeType, value?: any) => value === undefined ? ({ attributeType: ElementAttributeType.Invalid, value: null}) : ({ attributeType: type, value: value.toString()});

export class NodePacker extends TemplateNodeVisitor {

    private package: FormPack = new FormPack();

    private sparePage: PagePack = new PagePack();

    private spareElem: ElementPack = new ElementPack();

    public static buildPackage(form: FormNode): FormPack {
        const packer = new NodePacker();

        packer.visitFormNode(form);

        return packer.package;
    }

    private packPage = (node: PageNode): PagePack => {
        node.accept(this);
        return this.sparePage;
    }

    private packElem = (node: ElementNode): ElementPack => {
        node.accept(this);
        return this.spareElem;
    }

    private visitElem(node: ElementNode): ElementPack {
        const pack = new ElementPack();
        const element = new Element();

        element.dynamicName = node.dynamicName;
        element.formContextId = node.formContextId || 0;
        element.formInputName = node.formInputName;
        element.elementType = node.elementType;
        element.prototypeId = node.prototypeId || 0;
        element.parentFormContextId = node.parentFormContextId;

        const style = node.style;
        const dim = style.dimensions;
        const border = style.border;
        const radius = border.radii;
        const borderWidth = border.width;
        const padding = style.padding;

        const borderColorHex = border.color !== undefined ? border.color.toHex() : undefined;
        const backgroundColorHex = style.backgroundColor !== undefined ? style.backgroundColor.toHex() : undefined;

        element.attributes = [
            getAttr(ElementAttributeType.Text, node.text),
            getAttr(ElementAttributeType.TextStyle, node.textStyleName),
            getAttr(ElementAttributeType.IsHidden, node.isHidden),
            getAttr(ElementAttributeType.IsMultiline, node.isMultiline),
            getAttr(ElementAttributeType.MaxLength, node.maxLength),
            getAttr(ElementAttributeType.URL, node.url),

            getAttr(ElementAttributeType.Width, dim.width),
            getAttr(ElementAttributeType.Height, dim.height),

            getAttr(ElementAttributeType.BorderRadiusTopLeft, radius.topLeft),
            getAttr(ElementAttributeType.BorderRadiusTopRight, radius.topRight),
            getAttr(ElementAttributeType.BorderRadiusBottomRight, radius.bottomRight),
            getAttr(ElementAttributeType.BorderRadiusBottomLeft, radius.bottomLeft),

            getAttr(ElementAttributeType.BorderLeft, borderWidth.left),
            getAttr(ElementAttributeType.BorderTop, borderWidth.top),
            getAttr(ElementAttributeType.BorderRight, borderWidth.right),
            getAttr(ElementAttributeType.BorderBottom, borderWidth.bottom),

            getAttr(ElementAttributeType.BorderColor, borderColorHex),

            getAttr(ElementAttributeType.PaddingLeft, padding.left),
            getAttr(ElementAttributeType.PaddingTop, padding.top),
            getAttr(ElementAttributeType.PaddingRight, padding.right),
            getAttr(ElementAttributeType.PaddingBottom, padding.bottom),

            getAttr(ElementAttributeType.BackgroundColor, backgroundColorHex),
            getAttr(ElementAttributeType.HorizontalAlignment, style.horizontalAlignment),
            getAttr(ElementAttributeType.Style, style.className),

            getAttr(ElementAttributeType.DateFormat, node.dateFormat),
        ].filter(v => v.attributeType !== ElementAttributeType.Invalid);
        
        pack.children = node.children.map(this.packElem);
        pack.item = element;

        return pack;
    }

    private visitInput(node: InputNode<any>) {
        const elem = this.visitElem(node);

        elem.item.attributes = [
            ...elem.item.attributes,
            getAttr(ElementAttributeType.DefaultValue, node.defaultValue),
            getAttr(ElementAttributeType.IsDisabled, node.isDisabled),
            getAttr(ElementAttributeType.Placeholder, node.placeholder),
            getAttr(ElementAttributeType.RequiredMessage, node.requiredMessage)
        ]

        return elem;
    }
    
    visitFormNode(node: FormNode) {
        this.package = new FormPack();

        this.package.item = {
            id: node.primId !== undefined ? node.primId : 0,
            name: node.name !== undefined ? node.name : "",
            formTypeId: node.formType,
            bloomClientId: node.clientId !== undefined ? node.clientId : 0,
        } as Form;

        this.package.children = node.children.map(this.packPage);
    }
    
    visitPageNode(node: PageNode) {
        const pack = new PagePack();
        
        pack.item = {
            id: node.primId !== undefined ? node.primId : 0,
            name: node.name !== undefined ? node.name : "",
        } as Page;

        pack.children = node.children.map(this.packElem);
        this.sparePage = pack;
    }

    visitSectionNode(node: SectionNode) {
        this.spareElem = this.visitElem(node);
    }

    visitTableNode(node: TableNode) {
        this.spareElem = this.visitElem(node);
    }

    visitRowNode(node: RowNode) {
        this.spareElem = this.visitElem(node);
    }

    visitCellNode(node: CellNode) {
        this.spareElem = this.visitElem(node);
    }

    visitDropdownGroupNode(node: DropdownGroupNode) {
        this.spareElem = this.visitInput(node);
    }

    visitRadioButtonGroupNode(node: RadioButtonGroupNode) {
        this.spareElem = this.visitElem(node);
    }

    visitDropdownElementNode(node: DropdownElementNode) {
        this.spareElem = this.visitElem(node);
    }

    visitRadioButtonElementNode(node: RadioButtonElementNode) {
        this.spareElem = this.visitInput(node);
    }

    visitLabelNode(node: LabelNode) {
        this.spareElem = this.visitElem(node);
    }

    visitTextBoxNode(node: TextBoxNode) {
        this.spareElem = this.visitInput(node);
    }

    visitCheckboxNode(node: CheckboxNode) {
        this.spareElem = this.visitInput(node);
    }

    visitDateNode(node: DateNode) {
        this.spareElem = this.visitInput(node);
    }

    visitImageNode(node: ImageNode) {
        this.spareElem = this.visitElem(node);
    }

    visitInvalidNode(node: InvalidNode) {
        this.spareElem = this.visitElem(node);
    }
}